import { cn } from '@trafiflow/shared-ui'
import { Brain, Lightbulb, Wand2, Rocket, Check } from 'lucide-react'
import type { AdGenStageStatus } from '@/services/ad-generator/types'

// Map 14 technical stages to 4 user-meaningful phases
const PHASE_CONFIG = [
  {
    id: 'understand',
    label: 'Understand',
    icon: Brain,
    stages: ['preflight_validation', 'brand_discovery', 'meta_identity_resolution', 'brand_ad_collection', 'individual_ad_analysis', 'competitor_intelligence'],
    description: 'Learning about your brand',
  },
  {
    id: 'strategize',
    label: 'Strategize',
    icon: Lightbulb,
    stages: ['product_bible', 'strategy_selection', 'creative_blueprint', 'clip_bank_creation'],
    description: 'Planning your ad',
  },
  {
    id: 'create',
    label: 'Create',
    icon: Wand2,
    stages: ['ai_asset_synthesis', 'variant_generation', 'scoring_and_selection'],
    description: 'Generating variants',
  },
  {
    id: 'deliver',
    label: 'Deliver',
    icon: Rocket,
    stages: ['final_production'],
    description: 'Rendering video',
  },
]

interface PhaseProgressBarProps {
  stages: AdGenStageStatus[]
  currentStage: string
  jobStatus: string
}

export default function PhaseProgressBar({ stages, currentStage, jobStatus }: PhaseProgressBarProps) {
  // Calculate phase status based on stage statuses
  const getPhaseStatus = (phaseStages: string[]) => {
    const stageStatuses = phaseStages.map(name =>
      stages.find(s => s.name === name)?.status || 'pending'
    )

    if (stageStatuses.every(s => s === 'completed' || s === 'degraded' || s === 'skipped')) {
      return 'completed'
    }
    if (stageStatuses.some(s => s === 'failed')) {
      return 'failed'
    }
    if (stageStatuses.some(s => s === 'running')) {
      return 'running'
    }
    if (stageStatuses.some(s => s === 'completed' || s === 'degraded')) {
      return 'partial'
    }
    return 'pending'
  }

  // Get current phase insight
  const getCurrentPhaseInsight = (phaseStages: string[]) => {
    const runningStage = stages.find(s => phaseStages.includes(s.name) && s.status === 'running')
    if (runningStage?.insight) return runningStage.insight

    const completedStages = stages
      .filter(s => phaseStages.includes(s.name) && (s.status === 'completed' || s.status === 'degraded'))
      .reverse()

    return completedStages[0]?.insight || null
  }

  // Find current phase
  const currentPhaseIndex = PHASE_CONFIG.findIndex(phase =>
    phase.stages.includes(currentStage)
  )

  return (
    <div className="space-y-4">
      {/* Phase progress */}
      <div className="flex items-center gap-2">
        {PHASE_CONFIG.map((phase, idx) => {
          const status = getPhaseStatus(phase.stages)
          const Icon = phase.icon
          const isCurrent = idx === currentPhaseIndex && jobStatus === 'running'
          const isCompleted = status === 'completed'
          const isFailed = status === 'failed'

          return (
            <div key={phase.id} className="flex items-center flex-1">
              {/* Phase indicator */}
              <div className="flex flex-col items-center flex-1">
                <div
                  className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300',
                    isCompleted && 'bg-emerald-500 text-white',
                    isFailed && 'bg-red-500 text-white',
                    isCurrent && 'bg-blue-500 text-white ring-4 ring-blue-500/20',
                    !isCompleted && !isFailed && !isCurrent && 'bg-tf-bg-muted text-tf-text-muted',
                  )}
                >
                  {isCompleted ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Icon className={cn('w-5 h-5', isCurrent && 'animate-pulse')} />
                  )}
                </div>
                <span className={cn(
                  'mt-2 text-xs font-medium text-center',
                  isCompleted && 'text-emerald-500',
                  isFailed && 'text-red-500',
                  isCurrent && 'text-blue-500',
                  !isCompleted && !isFailed && !isCurrent && 'text-tf-text-muted',
                )}>
                  {phase.label}
                </span>
              </div>

              {/* Connector line */}
              {idx < PHASE_CONFIG.length - 1 && (
                <div className={cn(
                  'h-0.5 flex-1 mx-2 transition-colors duration-300',
                  idx < currentPhaseIndex || (isCompleted && idx === currentPhaseIndex)
                    ? 'bg-emerald-500'
                    : 'bg-tf-border',
                )} />
              )}
            </div>
          )
        })}
      </div>

      {/* Current phase insight */}
      {currentPhaseIndex >= 0 && jobStatus === 'running' && (
        <div className="text-center">
          <p className="text-xs text-tf-text-muted">
            {PHASE_CONFIG[currentPhaseIndex].description}
          </p>
          {getCurrentPhaseInsight(PHASE_CONFIG[currentPhaseIndex].stages) && (
            <p className="text-sm text-blue-400 mt-1 animate-pulse">
              {getCurrentPhaseInsight(PHASE_CONFIG[currentPhaseIndex].stages)}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export { PHASE_CONFIG }
